import React, { useEffect } from 'react';
import { useLocalisation } from '@3plearning/lingui-cdn';
import { DropdownMenu } from '@3plearning/react-components';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import orderBy from 'lodash/orderBy';

import useCourseVersion from '@helpers/hooks/use-course-version';
import {
  getGenericCoursesV2,
  setSelectedClass,
  setSelectedGroup,
  updateSortedStudentIds
} from '@containers/upf-assign/upf-assign-actions';

import AssignmentFilters from '@components/assignment-filters/assignment-filters';
import PulseLoader from '@components/pulse-loader';

import { getGroupHashKey, mapEntitiesToOptions, sortEntitiesByIds } from '@containers/entities/helpers';

import useManageLocalStorage from '@helpers/hooks/use-manage-local-storage';
import { LOCAL_STORAGE_KEYS } from '@helpers/constants';
import { findOption } from '@helpers/utils';

import { mapStudentIdsFromGroups } from './helpers';
import { DEFAULT_GROUP_ID } from './constants';
import getTranslations from './translations';

import ClassFilters from './class-filters';

import { DropdownTitle, FilterWrapper, LoadingWrapper, Root } from './styled';

const ClassGroupFilter = () => {
  /**
   * Store
   */
  const dispatch = useDispatch();
  const selectedProduct = useSelector(state => state.product, shallowEqual);
  const groups = useSelector(state => state.entitiesReducer.groups);
  const students = useSelector(state => state.entitiesReducer.students);
  const classrooms = useSelector(state => state.entitiesReducer.classrooms);
  const isLoadingClassrooms = useSelector(state => state.gatewaysReducer.isLoadingClassrooms);
  const sortDirection = useSelector(state => state.UPFAssignReducer.sortDirection);
  const sortValue = useSelector(state => state.UPFAssignReducer.sortValue);
  const selectedClass = useSelector(state => state.UPFAssignReducer.selectedClass, shallowEqual);
  const selectedGroup = useSelector(state => state.UPFAssignReducer.selectedGroup, shallowEqual);

  /**
   * Translation
   */
  const { i18n } = useLocalisation();
  const { CLASS_LABEL_TEXT, GROUP_LABEL_TEXT, WHOLE_CLASS_TEXT } = getTranslations({ i18n });

  /**
   * Local variables
   */
  const { groups: selectedClassroomGroupIds = [] } = classrooms[selectedClass.value] || {};
  const classRoomGroups = selectedClassroomGroupIds.map(groupId => groups[groupId]);
  const defaultGroupOption = { value: DEFAULT_GROUP_ID, label: WHOLE_CLASS_TEXT };
  const classroomOptions = orderBy(mapEntitiesToOptions(classrooms), [option => option.label.toLowerCase()], ['asc']);
  const groupOptions = [defaultGroupOption, ...mapEntitiesToOptions(classRoomGroups)];
  const { selectedClassroomId, selectedCourseId, selectedCourseVersion } = useCourseVersion();

  /**
   * Functions
   */
  const findStudentIdsFromSelectedGroup = () => {
    const isDefaultGroup = selectedGroup.value === DEFAULT_GROUP_ID;
    const groupKey = getGroupHashKey({ classroomId: selectedClassroomId, groupId: selectedGroup.value });
    const { students: studentIds = [] } = groups[groupKey] || {};
    return isDefaultGroup ? mapStudentIdsFromGroups(classRoomGroups) : studentIds;
  };

  const onGroupChange = selectedOption => {
    dispatch(setSelectedGroup(selectedOption));
  };

  const onClassroomChange = selectedOption => {
    dispatch(setSelectedClass(selectedOption));
    onGroupChange(defaultGroupOption);
  };

  /**
   * Hooks
   */
  useManageLocalStorage({
    checkShouldUpdateValueFromLocalStorage: localStorageValue => findOption(classroomOptions, localStorageValue),
    defaultValue: classroomOptions[0],
    enable: !!classroomOptions[0],
    key: LOCAL_STORAGE_KEYS.LAST_USED_CLASSROOM,
    updateValueFromLocalStorage: updatedValue => dispatch(setSelectedClass(updatedValue)),
    value: selectedClass
  });

  useManageLocalStorage({
    checkShouldUpdateValueFromLocalStorage: localStorageValue => findOption(groupOptions, localStorageValue),
    defaultValue: groupOptions[0],
    enable: !!(selectedClass?.value && groupOptions[1]),
    key: LOCAL_STORAGE_KEYS.LAST_USED_GROUP,
    updateValueFromLocalStorage: updatedValue => onGroupChange(updatedValue),
    value: selectedGroup
  });

  /**
   * Effects
   */
  useEffect(() => {
    if (selectedClassroomId && selectedCourseId) {
      dispatch(
        getGenericCoursesV2({
          productId: selectedProduct.id,
          courseId: selectedCourseId,
          courseVersion: selectedCourseVersion
        })
      );
    }
  }, [selectedClassroomId, selectedCourseId, selectedCourseVersion]);

  useEffect(() => {
    if (selectedGroup.value !== '' && !isLoadingClassrooms) {
      const updatedStudentIds = findStudentIdsFromSelectedGroup();
      const sortedStudentIds = sortEntitiesByIds({
        entities: students,
        ids: updatedStudentIds,
        direction: sortDirection,
        sortKey: sortValue
      });

      dispatch(updateSortedStudentIds(sortedStudentIds));
    }
  }, [selectedClassroomId, selectedGroup.value, isLoadingClassrooms]);

  return (
    <Root>
      {isLoadingClassrooms && (
        <LoadingWrapper>
          <PulseLoader />
        </LoadingWrapper>
      )}

      {!isLoadingClassrooms && (
        <>
          <FilterWrapper>
            <DropdownTitle>{CLASS_LABEL_TEXT}</DropdownTitle>
            <ClassFilters
              classroomOptions={classroomOptions}
              selectedClass={selectedClass}
              onClassroomChange={onClassroomChange}
            />
          </FilterWrapper>

          <FilterWrapper data-name="group-selector">
            <div>{GROUP_LABEL_TEXT}</div>
            <DropdownMenu options={groupOptions} selectedOption={selectedGroup} onSelectOption={onGroupChange} />
          </FilterWrapper>

          <AssignmentFilters i18n={i18n} />
        </>
      )}
    </Root>
  );
};

export default ClassGroupFilter;



// WEBPACK FOOTER //
// ./components/class-group-filters/index.js